/**
 * @fileoverview MTG Ravnica Minigame - Data-Driven Controller
 * @author Principal Architect
 */

class MtgCinematicMinigame {
  constructor(containerId) {
    this.container = document.getElementById(containerId);
    if (!this.container) return;

    this.table = this.container.querySelector('#MtgTable');
    this.header = this.container.querySelector('#MtgHeader');
    this.revealOverlay = this.container.querySelector('#MtgRevealOverlay');
    this.cards = Array.from(this.container.querySelectorAll('.mtg-card-wrapper'));
    
    this.codeDisplay = this.container.querySelector('#MtgDiscountCode');
    this.codeBox = this.container.querySelector('.mtg-reveal__code-box');
    this.copyBtn = this.container.querySelector('#MtgCopyBtn');
    this.revealHeadline = this.container.querySelector('[data-target="reveal-title"]');

    // Parse configuration from Liquid schema injection (Dataset)
    this.config = {
      probTreat: parseInt(this.container.dataset.probTreat || 80, 10) / 100,
      scale: parseFloat(getComputedStyle(this.container).getPropertyValue('--card-scale')) || 1.4,
      treat: {
        title: this.container.dataset.txtTreatTitle,
        rules: this.container.dataset.txtTreatRules,
        code: this.container.dataset.txtTreatCode,
        art: this.container.dataset.treatArt
      },
      trick: {
        title: this.container.dataset.txtTrickTitle,
        rules: this.container.dataset.txtTrickRules,
        code: this.container.dataset.txtTrickCode,
        art: this.container.dataset.trickArt
      }
    };

    // Load Audio dynamically
    this.audioContext = null;
    this.audioBuffers = {};
    this.audioUrls = {
      hover: this.container.dataset.audioHover,
      flip: this.container.dataset.audioFlip,
      treat: this.container.dataset.audioTreat,
      trick: this.container.dataset.audioTrick
    };

    this.isResolving = false;
    this.init();
  }

  init() {
    this.initAudio();

    this.cards.forEach(card => {
      card.addEventListener('mouseenter', () => this.playSound('hover'));
      card.addEventListener('click', (e) => this.initiateSequence(e.currentTarget));
    });

    this.copyBtn.addEventListener('click', () => this.copyToClipboard());
  }

  // --- AUDIO API (Non-blocking) ---
  initAudio() {
    try {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
      Object.keys(this.audioUrls).forEach(key => {
        if (this.audioUrls[key]) this.loadAudio(key, this.audioUrls[key]);
      });
    } catch (e) {
      console.warn('[MtgMinigame] Web Audio API unsupported.');
    }
  }

  async loadAudio(key, url) {
    try {
      const res = await fetch(url);
      const buf = await res.arrayBuffer();
      this.audioBuffers[key] = await this.audioContext.decodeAudioData(buf);
    } catch (e) {
      console.info(`[MtgMinigame] Audio skipped or failed to load: ${key}`);
    }
  }

  playSound(key) {
    if (!this.audioContext || !this.audioBuffers[key]) return;
    if (this.audioContext.state === 'suspended') this.audioContext.resume();
    const source = this.audioContext.createBufferSource();
    source.buffer = this.audioBuffers[key];
    source.connect(this.audioContext.destination);
    source.start(0);
  }

  // --- ANIMATION & STATE LOGIC ---
async initiateSequence(selectedCard) {
    if (this.isResolving) return;
    this.isResolving = true;
    this.playSound('flip');

    this.header.style.opacity = '0';
    this.table.classList.add('is-resolving');

    this.cards.forEach(c => {
      if (c !== selectedCard) c.classList.add('is-hidden');
    });

    // --- BULLETPROOF SPATIAL MATH ---
    const isMobile = window.innerWidth <= 900;
    const cardRect = selectedCard.getBoundingClientRect();
    const sectionRect = this.container.getBoundingClientRect();
    
    const cardCenterX = cardRect.left + (cardRect.width / 2);
    const cardCenterY = cardRect.top + (cardRect.height / 2);

    let targetX, targetY;

    if (isMobile) {
      // Mobile: Center top
      targetX = sectionRect.left + (sectionRect.width / 2);
      targetY = sectionRect.top + (sectionRect.height * 0.25);
    } else {
      // Desktop: Left 25% of the overall section
      targetX = sectionRect.left + (sectionRect.width * 0.25);
      targetY = sectionRect.top + (sectionRect.height / 2);
    }

    const deltaX = targetX - cardCenterX;
    const deltaY = targetY - cardCenterY;

    // 2D Spatial Move
    selectedCard.style.transform = `translate3d(${deltaX}px, ${deltaY}px, 0) scale(${this.config.scale})`;
    selectedCard.style.zIndex = '50';

    // 3D Flip
    const innerCard = selectedCard.querySelector('.mtg-card');
    requestAnimationFrame(() => {
      innerCard.style.transform = 'rotateY(180deg)';
    });

    await this.resolveOutcome(selectedCard);
  }

  async resolveOutcome(card) {
    await new Promise(resolve => setTimeout(resolve, 800));

    const isTreat = Math.random() < this.config.probTreat;
    const resultType = isTreat ? 'treat' : 'trick';
    const payload = this.config[resultType];
    
    card.querySelector('[data-target="name"]').textContent = payload.title;
    card.querySelector('[data-target="rules"]').textContent = payload.rules;
    
    // Robust Art Injection
    const artElement = card.querySelector('[data-target="art"]');
    if (payload.art && payload.art.trim() !== '') {
       artElement.style.backgroundImage = `url('${payload.art}')`;
    } else {
       artElement.style.background = '#111'; // Fallback if no art selected
    }

    this.triggerGrandReveal(payload.code, resultType);
  }

  async resolveOutcome(card) {
    // SECURITY NOTE: Client-side probability logic is used here so the merchant can 
    // test the UI via the Customizer. In production, this data should be fetched from a Proxy.
    await new Promise(resolve => setTimeout(resolve, 800)); // Simulating network latency

    const isTreat = Math.random() < this.config.probTreat;
    const resultType = isTreat ? 'treat' : 'trick';
    const payload = this.config[resultType];
    
    // Inject schema data into card face
    card.querySelector('[data-target="name"]').textContent = payload.title;
    card.querySelector('[data-target="rules"]').textContent = payload.rules;
    
    if (payload.art) {
       card.querySelector('[data-target="art"]').style.backgroundImage = `url('${payload.art}')`;
    }

    this.triggerGrandReveal(payload.code, resultType);
  }

  triggerGrandReveal(code, type) {
    this.playSound(type);
    
    // Apply styling based on Trick/Treat classes
    this.revealHeadline.textContent = type === 'treat' ? 'A Boon from the Guilds!' : 'A Gruul Ambush!';
    this.revealHeadline.className = `mtg-reveal__headline is-${type}`;
    this.codeBox.className = `mtg-reveal__code-box is-${type}`;

    this.codeDisplay.textContent = code;
    this.copyBtn.dataset.code = code;
    this.revealOverlay.classList.add('is-visible');
  }

  async copyToClipboard() {
    const code = this.copyBtn.dataset.code;
    if (!code) return;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(code);
      } else {
        const t = document.createElement("textarea");
        t.value = code;
        t.style.position = "fixed";
        t.style.opacity = 0;
        document.body.appendChild(t);
        t.select();
        document.execCommand('copy');
        t.remove();
      }
      this.copyBtn.textContent = 'Sigil Copied!';
      setTimeout(() => { this.copyBtn.textContent = 'Extract Sigil (Copy)'; }, 3000);
    } catch (err) {
      console.error(err);
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.mtg-minigame').forEach(el => new MtgCinematicMinigame(el.id));
});